import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, User } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/blogs');
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="blogs-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="blogs-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="blogs-hero-heading">
              Insights & Stories
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Expert advice on fabrics, manufacturing, and uniform trends from industry leaders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blogs Grid */}
      <section className="section-spacing" data-testid="blogs-grid">
        <div className="container-custom">
          {loading && (
            <div className="text-center py-20" data-testid="blogs-loading">
              <div className="w-12 h-12 border-4 border-brand-pearl/20 border-t-brand-pearl rounded-full animate-spin mx-auto mb-4" />
              <p className="text-brand-walnut">Loading articles...</p>
            </div>
          )}

          {!loading && blogs.length === 0 && (
            <div className="text-center py-20" data-testid="blogs-empty">
              <p className="text-lg text-brand-walnut">No articles found. Check back soon!</p>
            </div>
          )}

          {!loading && blogs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, idx) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="card-hover"
                  data-testid={`blog-card-${idx}`}
                >
                  <Link to={`/blog/${blog.slug}`} className="block">
                    <div className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden">
                      <div className="parallax-img">
                        <img src={blog.image_url} alt={blog.title} className="w-full h-48 object-cover" />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center space-x-4 text-xs text-brand-walnut mb-3">
                          <span className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            {blog.author}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(blog.created_at)}
                          </span>
                        </div>
                        <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-2 line-clamp-2">
                          {blog.title}
                        </h3>
                        <p className="text-sm text-brand-walnut line-clamp-3">{blog.excerpt}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;